import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Empty, Flex, message, Modal, Select, Spin } from 'antd'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useIdentityServiceGetProfilesListEndpointKey,
  usePersonsServiceCreatePersonUserAssignmentEndpoint,
  usePersonsServiceGetListPersonUserAssignmentsEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetProfilesListResponse,
  IdentityService,
  Permissions
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { SaveConfirm } from 'src/components'
import { useDebounce, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  doctorsIds: string[]
  onCancel: () => void
}

export const SelectDoctors = ({ doctorsIds, onCancel }: Props) => {
  const { t } = useLocales()
  const { id } = useParams()
  const { userData } = useUser()
  const queryClient = useQueryClient()

  const [searchTerm, setSearchTerm] = useState<string>()
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([])

  const debouncedSearch = useDebounce(searchTerm)

  const { mutate } = usePersonsServiceCreatePersonUserAssignmentEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetListPersonUserAssignmentsEndpointKey]
      })
      message.success(
        selectedDoctors.length > 1
          ? t('persons.added_doctors_successfully')
          : t('persons.added_doctor_successfully')
      )
      onCancel()
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useIdentityServiceGetProfilesListEndpointKey,
      String(id),
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await IdentityService.getProfilesListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        userData?.currentLegalEntityId,
        undefined,
        undefined,
        undefined,
        [Permissions.PERSONS_USER_ASSIGNMENT_USER_CAN_HAVE_PERSON_ASSIGNED]
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetProfilesListResponse | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!id
  })

  const selectOptions = useMemo(() => {
    if (!data) return []

    return data.pages.flatMap(
      (page) =>
        page.data
          ?.filter((item) => !doctorsIds.includes(String(item.id)))
          .map((item) => ({
            label: `${item.firstName} ${item.lastName}`,
            value: String(item.id)
          })) ?? []
    )
  }, [data, doctorsIds, searchTerm])

  const onSubmit = () => {
    if (selectedDoctors.length === 0) {
      message.warning(t('persons.select_doctor_warning'))
      return
    }

    mutate({ personId: String(id), requestBody: { userIds: selectedDoctors } })
  }

  return (
    <Modal
      open
      width={500}
      title={t('persons.add_doctor')}
      centered
      maskClosable={false}
      footer={false}
      onCancel={onCancel}
    >
      <Flex align="center" justify="center" vertical gap={16}>
        <Select
          mode="multiple"
          value={selectedDoctors}
          allowClear
          placeholder={t('persons.select_doctor')}
          className="full-width"
          options={selectOptions}
          showSearch
          searchValue={searchTerm}
          filterOption={false}
          onSearch={(value) => setSearchTerm(value)}
          loading={isFetching}
          onChange={(value) => setSelectedDoctors(value)}
          onPopupScroll={(e) => {
            const target = e.target as HTMLElement
            if (target.scrollTop + target.offsetHeight === target.scrollHeight)
              fetchNextPage()
          }}
          notFoundContent={
            isFetching ? (
              <Flex justify="center">
                <Spin spinning />
              </Flex>
            ) : (
              <Empty />
            )
          }
        />

        <SaveConfirm title={`${t('persons.add_doctor')}?`} onConfirm={onSubmit}>
          <Button>{t('persons.add_doctor')}</Button>
        </SaveConfirm>
      </Flex>
    </Modal>
  )
}
